// Service for fetching and parsing a single property from REMU
// (remuproperties.com — Bank of Cyprus's Real Estate Management Unit).
//
// REMU is an ASP.NET WebForms + DevExpress site (server-rendered HTML, no JSON
// listing API) fronted by an F5 BIG-IP WAF. The WAF lets a clean browser-like
// request through, so we fetch the detail page HTML via the shared CORS proxy
// (same path BuySell/Altia use) and parse it with regex.
//
// Every REMU listing is a bank-owned (REO) asset, so all properties are tagged
// sellerType: 'bank' / distress.acquired: true.

import { createNormalizedPropertyData } from './propertyDataSchema';
import { fetchViaProxies } from './proxyFetch';
import { sanitizeText } from './buysellService';

const REMU_ORIGIN = 'https://www.remuproperties.com';

/**
 * Extract the numeric listing ID from a REMU URL.
 * Supported: /Cyprus/listing-41086
 * @param {string} url - REMU property URL
 * @returns {string|null}
 */
export const extractRemuId = (url) => {
  if (!url) return null;
  const match = url.match(/\/listing-(\d+)/i);
  return match ? match[1] : null;
};

/** Strip HTML tags + entities from a REMU fragment, collapsing whitespace. */
const stripHtml = (s) =>
  (s || '')
    .replace(/<sup>([\s\S]*?)<\/sup>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/&#13;|&#10;/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Parse a REMU price string to euros. REMU uses European dot-thousands
 * ("€105.000" = 105000) with no decimals, so all non-digits are stripped.
 */
const parsePrice = (s) => {
  const digits = String(s || '').replace(/[^\d]/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

/**
 * Parse a REMU area value to m². The unit is rendered as "m<sup>2</sup>"
 * (→ "m2" after tag stripping), so take the digits before the "m" and strip
 * dot/comma thousands separators ("1.152m²" → 1152).
 */
const parseArea = (s) => {
  if (!s) return '';
  const before = String(s).split(/\s*m/i)[0];
  const digits = before.replace(/[^\d]/g, '');
  if (!digits) return '';
  const n = parseInt(digits, 10);
  return Number.isNaN(n) || n < 0 ? '' : String(n);
};

/** Parse a REMU integer attribute (beds/baths); returns '' when absent. */
const parseInteger = (s) => {
  if (!s) return '';
  const n = parseInt(String(s).replace(/[^\d]/g, ''), 10);
  return Number.isNaN(n) ? '' : String(n);
};

/**
 * Build a { label: value } map from REMU's rowtop(label)/row(value) grid.
 * A `<div class="row rowtop">` holds labels; the following `<div class="row">`
 * holds the matching values.
 */
const parseAttributes = (html) => {
  const map = {};
  const rowRe = /<div class="row rowtop">([\s\S]*?)<\/div>\s*<\/div>\s*<div class="row">([\s\S]*?)<\/div>\s*<\/div>/gi;
  let m;
  while ((m = rowRe.exec(html)) !== null) {
    const labels = [...m[1].matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].map((x) => stripHtml(x[1]));
    const values = [...m[2].matchAll(/<p[^>]*>([\s\S]*?)<\/p>/gi)].map((x) => stripHtml(x[1]));
    for (let i = 0; i < labels.length; i++) {
      if (labels[i]) map[labels[i]] = values[i] || '';
    }
  }
  return map;
};

/** Derive a location string from a REMU address/title. */
const buildLocation = (address, title) => {
  if (address) return address;
  // Titles look like "Three-Bedroom House in Korakou Nicosia".
  const m = (title || '').match(/\bin\s+(.+)$/i);
  if (m) return m[1].trim();
  return 'Cyprus';
};

/**
 * Detect sale/rent for a REMU property. REMU is a sales-only portal.
 * @param {string} url
 * @param {Object} [normalizedData]
 * @returns {{forSale: boolean, forRent: boolean}}
 */
export const detectRemuPropertyType = (url, normalizedData = null) => {
  if (normalizedData && (normalizedData.forSale || normalizedData.forRent)) {
    return { forSale: normalizedData.forSale, forRent: normalizedData.forRent };
  }
  return { forSale: true, forRent: false };
};

/**
 * Convert a REMU detail-page HTML string into normalized property data.
 * @param {string} html
 * @param {string} propertyId
 * @returns {Object} - Normalized property data
 */
export const remuListingToNormalized = (html, propertyId) => {
  const getId = (id) => {
    const m = html.match(new RegExp(`id="ContentPlaceHolder1_${id}"[^>]*>([\\s\\S]*?)<\\/`, 'i'));
    return m ? stripHtml(m[1]) : '';
  };

  const title = sanitizeText(getId('dTitle'), 200) || 'Untitled Property';
  const price = parsePrice(getId('dPrice'));
  const status = getId('dStatus').toLowerCase();
  const address = getId('dAddress');

  const descMatch = html.match(/id="ContentPlaceHolder1_fDescription"[^>]*>([\s\S]*?)<\/p>/i);
  const description = sanitizeText(stripHtml(descMatch ? descMatch[1] : ''), 3000);

  const labels = parseAttributes(html);
  // Houses use "Build Area"; apartments use "Area"; commercial uses "Covered area".
  const buildArea = parseArea(
    labels['Build Area'] || labels['Covered area'] || labels['Covered Area'] || labels['Area']
  );
  const landArea = parseArea(labels['Land area'] || labels['Plot area'] || labels['Plot Area']);
  const area = buildArea || landArea || '';
  const bedrooms = parseInteger(labels['No. of Bedrooms'] || labels['Bedrooms']);
  const bathrooms = parseInteger(labels['No. of Bathrooms'] || labels['Bathrooms']);

  let latitude = null;
  let longitude = null;
  const coordMatch = html.match(/"lat"\s*:\s*'?(-?\d+\.\d+)'?\s*,\s*"lng"\s*:\s*'?(-?\d+\.\d+)'?/i);
  if (coordMatch) {
    latitude = parseFloat(coordMatch[1]);
    longitude = parseFloat(coordMatch[2]);
  }

  const forRent = status.includes('rent');
  const forSale = !forRent;

  return createNormalizedPropertyData({
    title,
    description,
    price,
    priceOnRequest: price === 0,
    area,
    bedrooms,
    bathrooms,
    imageURL: `${REMU_ORIGIN}/Cyprus/imgHandler.ashx?ref=${propertyId}`,
    latitude,
    longitude,
    location: buildLocation(address, title),
    forSale,
    forRent,
    // Bank-owned REO inventory → bank seller, acquired asset.
    sellerType: 'bank',
    distress: { acquired: true },
  });
};

/**
 * Fetch a single REMU listing's detail page (through the CORS proxy) and
 * normalize it.
 * @param {string} propertyUrl - REMU property URL
 *   (e.g. https://www.remuproperties.com/Cyprus/listing-41086)
 * @returns {Promise<Object>} - Normalized property data
 */
export const fetchRemuData = async (propertyUrl) => {
  if (!propertyUrl || !propertyUrl.trim()) {
    throw new Error('Property URL is required');
  }

  const propertyId = extractRemuId(propertyUrl);
  if (!propertyId) {
    throw new Error('Invalid REMU URL: Cannot extract listing ID');
  }

  const detailUrl = `${REMU_ORIGIN}/Cyprus/listing-${propertyId}`;

  const html = await fetchViaProxies(detailUrl, {
    responseType: 'text',
    timeout: 20000,
    label: 'REMU',
    validate: (body) =>
      typeof body === 'string' &&
      body.length > 500 &&
      !body.includes('Request Rejected') &&
      body.includes('ContentPlaceHolder1_dTitle'),
  });

  return remuListingToNormalized(html, propertyId);
};
